import { useEffect, useMemo, useState } from "react";
import { Select, Tag } from "antd";
import { useTranslation } from "react-i18next";

import ApiAnalytics from "../../../../api/analytics";
import { useCan } from "../../../../common/utils/usePermissions";

import "./pre-sign-in-activity.css";

const PERIOD_OPTIONS = ["7d", "30d", "90d"];
const DEFAULT_PERIOD = "30d";

/**
 * Sign-in activity that happens before anyone is authenticated (LF360-1917).
 *
 * It lives on the Onboarding page rather than on Usage because these events carry no
 * project: they fire before there is a subject, and LF360-1860 deliberately stops the app
 * publishing `project_id` once logged out. On a project-scoped page the block therefore
 * showed identical numbers for every project, and had to print a paragraph explaining why.
 * Next to the onboarding questions it reads as the first step of the same journey instead.
 *
 * The data still comes from the usage endpoint - `generic` is computed there and arrives in
 * the same response - so this component is a relocation, not a new query.
 *
 * Shown with `analytics.monitoring`, the permission `/v1/analytics/usage` checks
 * (LF360-2024); the request is never issued for anybody else, rather than issued and
 * then hidden.
 */
const PreSignInActivity = () => {
  const { t } = useTranslation();
  const canMonitor = useCan("analytics.monitoring");

  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!canMonitor) return undefined;

    let isCurrent = true;
    setLoading(true);
    setFailed(false);

    ApiAnalytics.getUsageAnalytics(period)
      .then((response) => {
        if (!isCurrent) return;
        setData(response.data || null);
      })
      .catch((error) => {
        console.error("Load pre-sign-in activity error:", error);
        if (!isCurrent) return;
        setData(null);
        // Deliberately no notification: this block is a secondary panel on someone else's
        // page. A failure here must not throw a toast over the onboarding table, which is
        // what the reader actually came for.
        setFailed(true);
      })
      .finally(() => {
        if (isCurrent) setLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [period, canMonitor]);

  const environment = data?.environment || null;
  const generic = data?.generic || null;
  const reasons = Array.isArray(generic?.loginFailuresByReason)
    ? generic.loginFailuresByReason
    : [];

  const hasData =
    !!generic &&
    (generic.devices > 0 || generic.loginFailures > 0 || generic.passwordResetOpened > 0);

  // Rendered as plain bars rather than through the chart renderer. This sits on a table
  // page, above an infinite-scroll table that owns the rest of the viewport, so every
  // pixel it takes is a pixel of table the reader loses. A full chart component here is
  // both too tall and visually louder than the table it introduces.
  const reasonsMax = useMemo(
    () => reasons.reduce((max, item) => Math.max(max, item?.failures || 0), 0),
    [reasons]
  );

  if (!canMonitor) return null;

  return (
    <section className="pre-sign-in">
      <div className="pre-sign-in__head">
        <h3 className="pre-sign-in__title">
          {t("analytics.usage_dashboard.generic.title")}
        </h3>
        <div className="pre-sign-in__controls">
          {environment && (
            <Tag color="blue" style={{ margin: 0 }}>
              {t("analytics.usage_dashboard.environment_label")}:{" "}
              {t(`analytics.usage_dashboard.env.${environment}`, {
                defaultValue: environment,
              })}
            </Tag>
          )}
          <Select
            value={period}
            onChange={setPeriod}
            size="small"
            style={{ width: 140 }}
            options={PERIOD_OPTIONS.map((value) => ({
              value,
              label: t(`analytics.usage_dashboard.period.${value}`),
            }))}
          />
        </div>
      </div>

      {/* Stated on the page, not in a tooltip: these numbers describe the whole
          environment. Someone reading them alongside a project's onboarding figures needs
          to know that before reading them, not after. */}
      <p className="pre-sign-in__note">
        {t("analytics.usage_dashboard.generic.note")}
      </p>

      {loading && !data ? (
        <p className="pre-sign-in__empty">{t("analytics.usage_dashboard.loading")}</p>
      ) : failed ? (
        <p className="pre-sign-in__empty">{t("analytics.usage_dashboard.load_error")}</p>
      ) : hasData ? (
        <div className="pre-sign-in__body">
          <div className="pre-sign-in__stats">
            <div className="pre-sign-in__stat">
              <p className="pre-sign-in__stat-value">{generic.devices}</p>
              <p className="pre-sign-in__stat-label">
                {t("analytics.usage_dashboard.generic.devices")}
              </p>
            </div>
            <div className="pre-sign-in__stat">
              <p className="pre-sign-in__stat-value">{generic.loginFailures}</p>
              <p className="pre-sign-in__stat-label">
                {t("analytics.usage_dashboard.generic.failures")}
              </p>
            </div>
            <div className="pre-sign-in__stat">
              <p className="pre-sign-in__stat-value">{generic.passwordResetOpened}</p>
              <p className="pre-sign-in__stat-label">
                {t("analytics.usage_dashboard.generic.password_reset")}
              </p>
            </div>
          </div>

          {reasons.length > 0 && (
            <div className="pre-sign-in__reasons">
              <p className="pre-sign-in__reasons-title">
                {t("analytics.usage_dashboard.generic.reason_x_label")}
              </p>
              {reasons.map((item) => (
                <div className="pre-sign-in__reason" key={item.reason}>
                  <span className="pre-sign-in__reason-name">{item.reason}</span>
                  <span className="pre-sign-in__reason-track">
                    <span
                      className="pre-sign-in__reason-bar"
                      style={{
                        width: `${reasonsMax ? (item.failures / reasonsMax) * 100 : 0}%`,
                      }}
                    />
                  </span>
                  <span className="pre-sign-in__reason-value">{item.failures}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <p className="pre-sign-in__empty">{t("analytics.usage_dashboard.empty")}</p>
      )}
    </section>
  );
};

export default PreSignInActivity;
